import { Table, Dropdown } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "@/hooks/LoaderProvider";
import { deleteManifesto } from "@/store/redux/slices/manifesto/manifesto_slice";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import MedalIcon from "@/assets/icons/web_Icons/medal-star.svg";
import { useState } from "react";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { selectManifestoLoading } from "@/store/redux/slices/manifesto/manifesto_slice";

const ManifestoHomeTable = ({
  data,
  setSelectedManifestos,
  onDelete,
  onModify,
  onTableChange,
}) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [selectedManifesto, setSelectedManifesto] = useState(null);
  const [deletedManifestoName, setDeletedManifestoName] = useState("");
  const isLoading = useSelector(selectManifestoLoading);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // Filter data based on global search
  const filteredData = globalSearchTerm
    ? data.filter(
        (item) =>
          item.title?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.author?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          (item.campaigns &&
            item.campaigns.some((campaign) =>
              campaign.toLowerCase().includes(globalSearchTerm.toLowerCase())
            ))
      )
    : data;

  // Format date to show only month and year (no day)
  const formatPublishDate = (dateString) => {
    if (!dateString) return "";

    // Handle both date strings and already formatted strings
    let date;

    // If dateString is already in "Month Day, Year" format
    if (typeof dateString === "string" && dateString.includes(",")) {
      // Extract just the month and year from the existing string
      const parts = dateString.split(" ");
      if (parts.length >= 3) {
        // Return "Month, Year" format
        return `${parts[0]}, ${parts[2]}`;
      }
    }

    // Otherwise, format from a date object
    date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", { month: "long", year: "numeric" })
      .replace(" ", ", ");
  };

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedManifestos(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  const handleMenuClick = (menuItem, record) => {
    if (menuItem.key === "delete") {
      // Don't show our own confirmation - let the parent handle it
      onDelete(record.id, record.title);
    } else if (menuItem.key === "modify") {
      onModify(record.id);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedManifesto) {
      try {
        setDeleteModalVisible(false);
        showLoader();

        // Store the title for success message
        setDeletedManifestoName(selectedManifesto.title);

        // Dispatch the delete action
        await dispatch(deleteManifesto(selectedManifesto.id)).unwrap();

        hideLoader();

        // Show success modal
        setSuccessModalVisible(true);

        // Call parent's onDelete (for any additional handling)
        if (onDelete) {
          onDelete(selectedManifesto.id, selectedManifesto.title);
        }
      } catch (error) {
        console.error("Failed to delete manifesto:", error);
        hideLoader();
      }
    }
  };

  const handleSuccessClose = () => {
    setSuccessModalVisible(false);
    setSelectedManifesto(null);

    // Refresh the table if needed
    if (onTableChange) {
      onTableChange();
    }
  };

  return (
    <>
      <Table
        loading={isLoading}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData.map((item) => ({ ...item, key: item.id }))}
        rowKey="id">
        <Column
          title="Title and Description"
          dataIndex="title"
          key="title"
          render={(_, record) => (
            <Link
              to={`/dashboard/manifesto/${record.id}`}
              className="flex flex-col justify-start text-left align-start whitespace-nowrap">
              <span className="text-small sm:text-[10px] text-primary">
                #{record.id?.substring(0, 8)}
              </span>
              <h3 className="mt-1 font-semibold text-bold sm:text-base text-darkGrey truncate w-[200px]">
                {record.title}
              </h3>

              {/* Display publish date in format: Published on Month, Year */}
              <p className="mt-1 text-xs text-small text-darkGrey">
                Published on{" "}
                {formatPublishDate(record.publishDate || record.createdAt)}
              </p>
            </Link>
          )}
        />
        <Column
          title="Created By"
          dataIndex="author"
          key="author"
          render={(_, record) => (
            <Link
              to={`/dashboard/manifesto/${record.id}`}
              className="flex items-center gap-3">
              <Avatar
                src={record.avatar}
                alt={record.author || "Author"}
                size={32}
              />
              <span className="text-small font-bold">
                {record.author || "System User"}
              </span>
            </Link>
          )}
        />
        <Column
          title="Campaigns"
          key="campaigns"
          dataIndex="campaigns"
          render={(_, record) => (
            <Link
              to={`/dashboard/manifesto/${record.id}`}
              className="grid grid-cols-2 gap-2">
              {record.campaigns && record.campaigns.length > 0 ? (
                record.campaigns.map((campaign, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-md p-1 flex items-center gap-2">
                    <img src={MedalIcon} alt="" />
                    <span className="whitespace-nowrap text-small font-bold">
                      {campaign}
                    </span>
                  </div>
                ))
              ) : (
                <span className="whitespace-nowrap text-small font-bold">
                  No campaigns linked
                </span>
              )}
            </Link>
          )}
        />
        <Column
          title="Status"
          key="status"
          dataIndex="status"
          render={(_, record) => (
            <Link
              to={`/dashboard/manifesto/${record.id}`}
              className="flex items-center gap-1">
              <div
                className={`w-2 h-2 rounded-full ${
                  record.isExpired ? "bg-danger" : "bg-primary"
                }`}
              />
              <p
                className={`${
                  record.isExpired ? "text-danger" : "text-primary"
                } text-small font-bold`}>
                {record.status}
              </p>
            </Link>
          )}
        />
        <Column
          title=""
          key="action"
          render={(_, record) => (
            <Dropdown
              menu={{
                items: [
                  {
                    key: "modify",
                    label: "Modify",
                    onClick: (e) => {
                      e.domEvent.stopPropagation();
                      handleMenuClick({ key: "modify" }, record);
                    },
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    onClick: (e) => {
                      e.domEvent.stopPropagation();
                      handleMenuClick({ key: "delete" }, record);
                    },
                  },
                ],
              }}
              trigger={["click"]}>
              <CustomMoreOptions
                variant="filled"
                onClick={(e) => e.stopPropagation()}
                color="black"
                orientation="vertical"
                size="small"
              />
            </Dropdown>
          )}
        />
      </Table>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Manifesto?"
        paragraph={
          selectedManifesto
            ? `Are you sure you want to delete "${selectedManifesto.title}"?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={successModalVisible}
        onClose={handleSuccessClose}
        header="Manifesto Deleted"
        paragraph={`"${deletedManifestoName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />
    </>
  );
};

ManifestoHomeTable.propTypes = {
  setSelectedManifestos: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      publishDate: PropTypes.string,
      createdAt: PropTypes.string,
      description: PropTypes.string,
      fileUrl: PropTypes.string,
      status: PropTypes.string,
      campaigns: PropTypes.arrayOf(PropTypes.string),
      author: PropTypes.string,
      avatar: PropTypes.string,
    })
  ),
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
  onTableChange: PropTypes.func,
};

export default ManifestoHomeTable;
